/* eslint-disable react-hooks/exhaustive-deps */
/*
  ArchetypeBrowse / ArchetypeDetail — public-facing archetype pages.

  Strategy:
    - /archetypes — public listing of all 41 archetypes grouped by category
      with short summary cards. CTA at top + bottom.
    - /archetypes/:key — public preview (name, category, short definition,
      teaser signals) with signup wall + sample callouts. If the visitor IS
      logged in, fetches the AI-generated detailed write-up instead and
      renders the full scientific deep-dive.
*/
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Heart, AlertCircle, CheckCircle2, Sparkles, BookOpen, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { SeoHead } from "@/components/SeoHead";
import { useAuth } from "@/context/AuthContext";

const CAT_LABEL = {
  withdrawal: "Withdrawal patterns",
  control: "Control patterns",
  inconsistency: "Inconsistency patterns",
  transactional: "Transactional patterns",
  emotional: "Emotional patterns",
  power: "Power & status patterns",
  intimacy: "Intimacy patterns",
  growth: "Growth patterns",
};

// ─── Public listing ──────────────────────────────────────────────────────
export function ArchetypeBrowse() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/archetypes/by-category").then((r) => {
      setGroups(r.data.groups || {});
      const cats = r.data.categories;
      // Backend returns categories as either {key: label} dict or array of keys.
      setCategories(Array.isArray(cats) ? cats : Object.keys(cats || {}));
    });
  }, []);

  const flat = useMemo(() => {
    if (!groups) return [];
    const out = [];
    Object.keys(groups).forEach((cat) => (groups[cat] || []).forEach((a) => out.push(a)));
    return out;
  }, [groups]);

  const visible = useMemo(() => {
    let list = flat;
    if (filter !== "all") list = list.filter((a) => a.category === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((a) => (a.name || "").toLowerCase().includes(needle) || (a.description || "").toLowerCase().includes(needle));
    }
    return list;
  }, [flat, filter, q]);

  return (
    <div data-testid="archetypes-browse" className="overflow-x-hidden">
      <SeoHead
        title="The 41 archetypes — a private taxonomy of complex dating dynamics"
        description="Forty-one named relationship patterns from withdrawal and inconsistency to power, intimacy and growth. Used by SugarSense's private AI strategist to read the dynamic in seconds."
        path="/archetypes"
      />

      {/* Hero */}
      <section className="ss-bg-radiant border-b border-champagne">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">A private taxonomy</p>
          <h1 className="mt-3 font-heading text-4xl sm:text-6xl text-cocoa leading-[1.05]">
            41 archetypes. <span className="italic brand-shine">One quiet way to read the dynamic.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-cocoa/70 text-base sm:text-lg leading-relaxed">
            SugarSense's archetype library is the spine of how we read a relationship pattern.
            Each archetype is observable, named, and grounded in well-established behavioural
            frameworks — attachment theory, intermittent reinforcement, schema therapy and
            social-exchange research. Browse them, learn what you're seeing, and pick yours.
          </p>
          {!user && (
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><button data-testid="archetypes-cta-register" className="ss-pill ss-pill-primary h-12 px-7">Sign up to read in full <ArrowRight size={16} /></button></Link>
              <Link to="/essays"><button className="ss-pill ss-pill-ghost h-12">Read the editorial</button></Link>
            </div>
          )}
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-champagne bg-blush/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center gap-2">
          <input
            data-testid="archetypes-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && q.trim().length >= 2) nav(`/search?q=${encodeURIComponent(q.trim())}`); }}
            placeholder="Search archetypes… (press Enter to search everywhere)" type="search"
            className="bg-ivory border border-champagne rounded-full h-10 px-5 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none text-sm flex-1 min-w-[160px] max-w-md"
          />
          <button onClick={() => setFilter("all")} className={`px-4 h-10 rounded-full text-xs uppercase tracking-widest ${filter === "all" ? "bg-cocoa text-ivory" : "bg-champagne/60 text-cocoa/70"}`}>All</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`archetypes-filter-${c}`}
              className={`px-4 h-10 rounded-full text-xs uppercase tracking-widest ${filter === c ? "bg-cocoa text-ivory" : "bg-champagne/60 text-cocoa/70"}`}>
              {CAT_LABEL[c] || c}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {!groups && <p className="text-cocoa/50 text-center">Loading the library…</p>}
        {groups && visible.length === 0 && <p className="text-cocoa/50 text-center italic">No archetypes match.</p>}

        {filter === "all" && !q.trim() && groups ? (
          // Grouped view
          <div className="space-y-14">
            {categories.map((cat) => {
              const items = groups[cat] || [];
              if (!items.length) return null;
              return (
                <div key={cat}>
                  <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">{CAT_LABEL[cat] || cat}</p>
                  <h2 className="mt-2 font-heading text-2xl sm:text-3xl text-cocoa">{cat.charAt(0).toUpperCase() + cat.slice(1)}</h2>
                  <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((a) => <ArchCard key={a.key} a={a} />)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map((a) => <ArchCard key={a.key} a={a} />)}
          </div>
        )}
      </section>

      {/* Conversion rail */}
      {!user && (
        <section className="border-t border-champagne bg-blush/15">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
            <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Curious about one of these?</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-cocoa leading-tight">
              The full pattern read takes five minutes.
            </h2>
            <p className="mt-4 text-cocoa/70 leading-relaxed max-w-xl mx-auto">
              Each archetype unlocks a deep, scientifically-grounded write-up — what it really is,
              how it usually arcs, what research says, and the elegant response. Free to start.
            </p>
            <Link to="/register">
              <button className="mt-7 ss-pill ss-pill-primary h-12 px-7">Sign up — free <ArrowRight size={16} /></button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

function ArchCard({ a }) {
  return (
    <Link to={`/archetypes/${a.key}`} data-testid={`arch-card-${a.key}`} className="ss-card-premium p-5 group block">
      <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{CAT_LABEL[a.category] || a.category}</p>
      <h3 className="mt-2 font-heading text-xl text-cocoa group-hover:text-mauve transition-colors leading-tight">{a.name}</h3>
      <p className="mt-2 text-cocoa/65 text-sm leading-relaxed line-clamp-3">{a.description}</p>
      <p className="mt-4 text-[11px] tracking-widest uppercase text-mauve">Read pattern →</p>
    </Link>
  );
}


// ─── Public detail page ──────────────────────────────────────────────────
export function ArchetypeDetail() {
  const { key } = useParams();
  const { user } = useAuth();
  const [arch, setArch] = useState(null);
  const [detail, setDetail] = useState(null); // populated only if logged in
  const [preview, setPreview] = useState(null); // public teaser
  const [err, setErr] = useState("");

  useEffect(() => {
    setArch(null); setDetail(null); setPreview(null); setErr("");
    api.get(`/archetypes/${key}`)
      .then((r) => setArch(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Archetype not found"));
    api.get(`/archetypes/${key}/preview`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [key]);

  const [search] = useSearchParams();
  const readerProfileId = search.get("profile_id") || "";
  useEffect(() => {
    if (!user || !arch) return;
    api.get(`/archetypes/${key}/detail`, { params: readerProfileId ? { profile_id: readerProfileId } : {} })
      .then((r) => setDetail(r.data))
      .catch(() => setDetail({ fallback: true }));
  }, [user, arch, key, readerProfileId]);

  if (err) return <div className="max-w-2xl mx-auto px-4 py-24 text-center text-cocoa/60">
    <p>{err}</p><Link to="/archetypes" className="mt-6 inline-block text-mauve underline">← Back to the library</Link>
  </div>;
  if (!arch) return <div className="max-w-2xl mx-auto px-4 py-24 text-cocoa/40">Reading the pattern…</div>;

  const d = detail?.detail;

  return (
    <article data-testid={`arch-detail-${key}`} data-rendered-pronouns={detail?.rendered_pronouns || ""} className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
      {detail?.rendered_pronouns && detail.rendered_pronouns !== "they/them" && (
        <p data-testid="arch-detail-rendered-pronouns" className="mb-4 text-[11px] tracking-[0.3em] uppercase text-mauve">Written in {detail.rendered_pronouns} for your saved person</p>
      )}
      <SeoHead
        title={`${arch.name} — relationship pattern`}
        description={arch.description}
        path={`/archetypes/${key}`}
      />
      <Link to="/archetypes" className="text-xs tracking-widest uppercase text-mauve"><ArrowLeft size={11} className="inline -mt-0.5" /> The library</Link>
      <p className="mt-6 text-[11px] tracking-[0.4em] uppercase text-mauve">{CAT_LABEL[arch.category] || arch.category}</p>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl text-cocoa leading-[1.05]">{arch.name}</h1>
      <p className="mt-6 text-cocoa/75 text-base sm:text-lg leading-relaxed">{arch.description}</p>

      {/* Fact-check provenance badge (auth + cached + passed) */}
      {user && detail && detail.fact_checked && (
        <div data-testid="arch-factcheck-badge" className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage/20 border border-sage/30 text-[10px] tracking-[0.25em] uppercase text-cocoa/70">
          <CheckCircle2 size={11} className="text-sage-dark" /> Editorially fact-checked
        </div>
      )}

      {/* Signals teaser — public */}
      {arch.signals?.length > 0 && (
        <section className="mt-10 ss-card p-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Signals you may notice</p>
          <ul className="mt-3 space-y-2 text-cocoa/80 text-sm">
            {arch.signals.slice(0, user ? arch.signals.length : 5).map((s, i) => (
              <li key={i} className="flex gap-2"><Sparkles size={11} className="mt-1 text-mauve shrink-0" />{s}</li>
            ))}
          </ul>
        </section>
      )}

      {/* PUBLIC — editorial preview of the long write-up (first ~120 words) */}
      {!user && preview?.long_overview_preview && (
        <section data-testid={`arch-preview-${key}`} className="mt-10 ss-article">
          <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">An editorial read</p>
          <h2 className="mt-2 font-heading text-2xl text-cocoa">What this looks like in practice</h2>
          <div className="mt-4 text-cocoa/80 leading-relaxed whitespace-pre-line">
            {preview.long_overview_preview}
            <span className="text-cocoa/50 italic"> (full outline available to members)</span>
          </div>
        </section>
      )}

      {/* AUTHENTICATED — full AI detail */}
      {user && detail && d && (
        <div data-testid={`arch-full-${key}`} className="mt-12 space-y-10">
          <section>
            <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">In depth</p>
            <h2 className="mt-2 font-heading text-2xl text-cocoa">Long view</h2>
            <div className="mt-3 text-cocoa/80 leading-relaxed whitespace-pre-line">{d.long_overview}</div>
          </section>

          <section className="ss-card p-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Psychological basis</p>
            <p className="mt-2 text-cocoa/80 leading-relaxed">{d.psychological_basis}</p>
          </section>

          {d.typical_arc && (
            <section>
              <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">The arc</p>
              <h2 className="mt-2 font-heading text-2xl text-cocoa">How this usually unfolds</h2>
              <div className="mt-5 relative">
                <div className="absolute left-3 top-1 bottom-1 w-px bg-champagne hidden sm:block" />
                <div className="space-y-5">
                  {[
                    { k: "early",  label: "Early (first 4–8 weeks)" },
                    { k: "middle", label: "Middle (established phase)" },
                    { k: "late",   label: "Late (deterioration or recycle)" },
                  ].map((s, i) => (
                    <div key={s.k} className="relative pl-0 sm:pl-10">
                      <div className="hidden sm:flex absolute left-0 top-1 w-6 h-6 rounded-full bg-mauve text-ivory items-center justify-center text-[10px] font-heading">{i + 1}</div>
                      <div className="ss-card p-5">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{s.label}</p>
                        <p className="mt-2 text-cocoa/80 leading-relaxed">{d.typical_arc[s.k]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {d.what_research_says && (
            <section className="ss-card-premium p-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">What research says</p>
              <p className="mt-2 text-cocoa/80 leading-relaxed">{d.what_research_says}</p>
              {d.sources_consulted?.length > 0 && (
                <p className="mt-3 text-[11px] text-cocoa/50 italic">
                  Sources consulted: {d.sources_consulted.join(" · ")}
                </p>
              )}
              <Link to="/research" className="mt-4 inline-block text-[11px] tracking-[0.3em] uppercase text-mauve hover:text-cocoa">
                The frameworks behind every reading →
              </Link>
            </section>
          )}

          {d.common_misreadings?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl text-cocoa">Often misread as</h2>
              <ul className="mt-3 space-y-2 text-cocoa/80 text-sm">
                {d.common_misreadings.map((m, i) => (
                  <li key={i} className="flex gap-2"><AlertCircle size={12} className="mt-1 text-mauve shrink-0" />{m}</li>
                ))}
              </ul>
            </section>
          )}

          {d.elegant_response && (
            <section className="ss-card p-6 bg-mauve/5">
              <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">The elegant response</p>
              <p className="mt-2 text-cocoa/80 leading-relaxed italic">{d.elegant_response}</p>
            </section>
          )}

          {d.red_flags?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl text-cocoa">Clear red flags</h2>
              <ul className="mt-3 space-y-2 text-cocoa/80 text-sm">
                {d.red_flags.map((r, i) => (
                  <li key={i} className="flex gap-2"><AlertCircle size={12} className="mt-1 text-rose shrink-0" />{r}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {user && detail && (detail.error || detail.fallback) && (
        <div className="mt-10 ss-card p-6 text-cocoa/60 text-sm">
          The deep-dive write-up is being prepared. Try again in a moment.
        </div>
      )}

      {/* PUBLIC — signup wall */}
      {!user && (
        <section data-testid={`arch-wall-${key}`} className="mt-14 ss-card-premium p-8 sm:p-10 text-center bg-blush/15">
          <Shield className="mx-auto text-mauve" size={26} strokeWidth={1.5} />
          <p className="mt-4 text-[11px] tracking-[0.4em] uppercase text-mauve">The full pattern read is for members</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-cocoa leading-tight">
            See the long view, the typical arc, the elegant response.
          </h2>
          <p className="mt-4 text-cocoa/70 max-w-xl mx-auto leading-relaxed">
            Each archetype unlocks a scientifically-grounded write-up — what it really is, how it
            usually arcs, what behavioural research says, common misreadings, and the elegant
            response. Free to start. Private to you.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"><button className="ss-pill ss-pill-primary h-12 px-7 w-full sm:w-auto">Sign up free <ArrowRight size={16} /></button></Link>
            <Link to="/login"><button className="ss-pill ss-pill-ghost h-12 w-full sm:w-auto">I have an account</button></Link>
          </div>
        </section>
      )}
    </article>
  );
}
